import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Spin } from 'antd';
const SQUARE_INIT_ERROR_MESSAGE = "We couldn't load the credit card form. Please try again.";
const SQUARE_SCRIPT_TIMEOUT_MS = 10000;
const SquarePaymentForm = (props) => {
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initError, setInitError] = useState(null);
    const cardRef = useRef(null);
    const mountedRef = useRef(false);
    const hiddenButtonRef = useRef(null);
    const cardContainerIdRef = useRef(`square-card-container-${Math.random().toString(36).slice(2)}`);
    const setButtonRefs = useCallback((node) => {
        hiddenButtonRef.current = node;
        if (typeof props.buttonRef === 'function') {
            props.buttonRef(node);
        }
        else if (props.buttonRef && 'current' in props.buttonRef) {
            props.buttonRef.current = node;
        }
    }, [props.buttonRef]);
    const initializeCard = useCallback(async (payments) => {
        const card = await payments.card();
        await card.attach(`#${cardContainerIdRef.current}`);
        return card;
    }, []);
    const invalidateSquareScript = useCallback(() => {
        const existingScript = document.querySelector(`script[src="${props.script_url}"]`);
        if (!existingScript) {
            return;
        }
        existingScript.dataset.failed = 'true';
        existingScript.dataset.loaded = 'false';
        existingScript.remove();
    }, [props.script_url]);
    const ensureSquareScript = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (window['Square']) {
                resolve();
                return;
            }
            const existingScript = document.querySelector(`script[src="${props.script_url}"]`);
            const attachScriptListeners = (script) => {
                const cleanup = () => {
                    window.clearTimeout(timeoutId);
                    script.removeEventListener('load', handleLoad);
                    script.removeEventListener('error', handleError);
                };
                const handleLoad = () => {
                    script.dataset.loaded = 'true';
                    script.dataset.failed = 'false';
                    cleanup();
                    resolve();
                };
                const handleError = () => {
                    script.dataset.failed = 'true';
                    cleanup();
                    reject(new Error('Square.js failed to load properly'));
                };
                const timeoutId = window.setTimeout(() => {
                    script.dataset.failed = 'true';
                    script.dataset.loaded = 'false';
                    cleanup();
                    reject(new Error('Timed out while loading Square.js'));
                }, SQUARE_SCRIPT_TIMEOUT_MS);
                script.addEventListener('load', handleLoad);
                script.addEventListener('error', handleError);
            };
            if (existingScript) {
                if (existingScript.dataset.failed === 'true') {
                    existingScript.remove();
                }
                else if (existingScript.dataset.loaded === 'true') {
                    resolve();
                    return;
                }
                else {
                    attachScriptListeners(existingScript);
                    return;
                }
            }
            const squareScript = document.createElement('script');
            squareScript.src = props.script_url;
            squareScript.type = 'text/javascript';
            squareScript.async = false;
            document.head.appendChild(squareScript);
            attachScriptListeners(squareScript);
        });
    }, [props.script_url]);
    const loadSquareCard = useCallback(async () => {
        if (!mountedRef.current) {
            return;
        }
        setLoading(true);
        setInitError(null);
        setErrors([]);
        try {
            cardRef.current?.destroy?.();
            cardRef.current = null;
            await ensureSquareScript();
            if (!window['Square']) {
                invalidateSquareScript();
                throw new Error('Square.js failed to load properly');
            }
            const payments = window['Square'].payments(props.app_id, props.location_id);
            cardRef.current = await initializeCard(payments);
        }
        catch (error) {
            cardRef.current = null;
            if (mountedRef.current) {
                setInitError(SQUARE_INIT_ERROR_MESSAGE);
            }
        }
        finally {
            if (mountedRef.current) {
                setLoading(false);
            }
        }
    }, [ensureSquareScript, initializeCard, invalidateSquareScript, props.app_id, props.location_id]);
    /**
     This function tokenizes a payment method.
     * @param paymentMethod
     */
    const tokenize = useCallback(async (paymentMethod) => {
        const tokenResult = await paymentMethod.tokenize();
        if (tokenResult.status === 'OK') {
            return tokenResult.token;
        }
        setErrors(tokenResult.errors);
        return null;
    }, []);
    const handlePaymentMethodSubmission = useCallback(async (event, paymentMethod) => {
        event.preventDefault();
        if (!paymentMethod) {
            setInitError(SQUARE_INIT_ERROR_MESSAGE);
            return;
        }
        setErrors([]);
        let token = null;
        try {
            token = await tokenize(paymentMethod);
        }
        catch (e) {
            token = null;
        }
        try {
            await props.onPayment(token);
        }
        catch (e) {
            return;
        }
    }, [props.onPayment, tokenize]);
    /**
     * Load required scripts
     */
    useEffect(() => {
        mountedRef.current = true;
        void loadSquareCard();
        return () => {
            mountedRef.current = false;
            cardRef.current?.destroy?.();
            cardRef.current = null;
        };
    }, [loadSquareCard]);
    /**
     * Add listener to hidden button
     */
    useEffect(() => {
        const cardButton = hiddenButtonRef.current;
        if (!cardButton) {
            return;
        }
        const onClick = async (event) => {
            await handlePaymentMethodSubmission(event, cardRef.current);
        };
        cardButton.addEventListener('click', onClick);
        return () => {
            cardButton.removeEventListener('click', onClick);
        };
    }, [handlePaymentMethodSubmission]);
    return (React.createElement(React.Fragment, null,
        loading && React.createElement(Spin, { size: "small" }),
        React.createElement("form", { id: "payment-form" },
            React.createElement("div", { id: cardContainerIdRef.current }),
            React.createElement("button", { style: { display: 'none' }, id: "card-button", type: "button", ref: setButtonRefs }),
            initError && (React.createElement("div", { className: "flex flex-col my-2" },
                React.createElement(Alert, { type: "error", message: initError, action: React.createElement(Button, { size: "small", type: "primary", onClick: () => void loadSquareCard() }, "Retry") }))),
            errors.length > 0 && (React.createElement("div", { className: "flex flex-col my-2" }, errors.map(e => {
                return (React.createElement("div", { key: e.type, className: "mt-1" },
                    React.createElement(Alert, { type: "error", message: e.message })));
            }))))));
};
export default SquarePaymentForm;
